import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/Dialog";
import { Skeleton } from "@/components/ui/Skeleton";
import { CheckCircle, Circle, Download, Eye, FileText, Loader2 } from "lucide-react";
import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useState } from "react";

import { filesService, FileType, KNOWLEDGE_BASE_FILE_TYPE_PARAM } from "@/api/files";
import type { TFileResponse } from "@/api/files/files.types";
import { Button } from "@/components/ui/Button";
import { extractErrorMessage } from "@/utils/errorUtils";
import { triggerBlobDownload, triggerBlobPreview } from "@/utils/downloadBlob";
import { useStore } from "@/store";
import { useNavigate } from "@tanstack/react-router";

/**
 * Answers a founder types into a to-do task are rendered to a document named after the task's
 * id, e.g. `task_response_82db77f7-3c84-4726-a007-22492e118e8b.pdf`. That name is storage
 * bookkeeping, not something to show a founder — the task title lives in the todo module and
 * isn't on this response, so the row is titled by what the file is instead.
 */
const GENERATED_TASK_RESPONSE_NAME = /^task_response_[0-9a-f-]{36}\.(pdf|txt)$/i;

/**
 * The knowledge base is grouped by where a file came from. Order is the order a founder meets
 * these surfaces: they onboard, then upload, then chat, then work through to-dos.
 */
const KNOWLEDGE_CATEGORIES = [
  { id: FileType.ONBOARDING_ANSWER, tab: "Onboarding", source: "Onboarding" },
  { id: FileType.COMPANY_DOCUMENT, tab: "Uploaded", source: "Uploaded" },
  { id: FileType.CHAT_ATTACHMENT, tab: "Chat", source: "Chat" },
  // The tab names a folder of files; the row names the one file in it.
  { id: FileType.TODO_TASK_RESPONSE, tab: "To-dos", source: "To-do" },
] as const;

const FILE_SOURCE_LABELS: Partial<Record<FileType, string>> = Object.fromEntries(
  KNOWLEDGE_CATEGORIES.map(({ id, source }) => [id, source])
);

const ALL_TAB_ID = "all";

/** Row title and provenance label. The title says what the file is; the label says where it came from. */
function describeFile(file: TFileResponse): { title: string; source: string | null } {
  const isGeneratedTaskResponse =
    file.file_type === FileType.TODO_TASK_RESPONSE && GENERATED_TASK_RESPONSE_NAME.test(file.original_filename);

  return {
    title: isGeneratedTaskResponse ? "Written answer" : file.original_filename,
    source: FILE_SOURCE_LABELS[file.file_type] ?? null,
  };
}

function isBrowserPreviewable(contentType: string, filename: string): boolean {
  if (contentType.startsWith("image/") || contentType === "application/pdf") {
    return true;
  }

  const extension = filename.split(".").pop()?.toLowerCase() ?? "";
  return ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "pdf"].includes(extension);
}

type TCompanyKnowledgeListProps = {
  companyId: string;
  companyName?: string | null;
  maxItems?: number;
  /** When false, hide the "Company Knowledge" heading (e.g. when used inside a card that already has the title). Default true. */
  showTitle?: boolean;
  /** Optional refetch trigger; when it changes, list will refetch (e.g. after upload). */
  refetchTrigger?: number;
};

export function CompanyKnowledgeList({
  companyId,
  companyName: _companyName,
  maxItems = 5,
  showTitle = true,
  refetchTrigger,
}: TCompanyKnowledgeListProps) {
  const navigate = useNavigate();
  const getCompanyById = useStore((state) => state.getCompanyById);
  const [files, setFiles] = useState<TFileResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingFileId, setLoadingFileId] = useState<string | null>(null);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB_ID);

  const fetchFiles = useCallback(async () => {
    if (!companyId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await filesService.listFiles({ company_id: companyId, file_type: KNOWLEDGE_BASE_FILE_TYPE_PARAM });
      if (response.error) {
        const errorMessage = await extractErrorMessage(response.error);
        setError(errorMessage);
        setFiles([]);
      } else if (response.data?.data) {
        const sortedFiles = [...response.data.data.files].sort((a, b) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return dateB - dateA;
        });
        setFiles(sortedFiles);
      } else {
        setFiles([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load files";
      setError(errorMessage);
      setFiles([]);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void fetchFiles();
  }, [fetchFiles, refetchTrigger]);

  const fetchFileBlob = async (file: TFileResponse) => {
    const { data: blob, error: downloadError } = await filesService.downloadFile(file.id);
    if (downloadError || !blob) {
      const msg = downloadError ? await extractErrorMessage(downloadError) : "Unable to open file";
      setError(msg);
      return null;
    }

    if (blob.type || !file.content_type) {
      return blob;
    }

    return new Blob([blob], { type: file.content_type });
  };

  const handlePreviewFile = async (file: TFileResponse) => {
    setLoadingFileId(file.id);
    try {
      const blob = await fetchFileBlob(file);
      if (!blob) return;

      if (isBrowserPreviewable(file.content_type, file.original_filename)) {
        triggerBlobPreview(blob, file.original_filename);
        return;
      }

      triggerBlobDownload(blob, file.original_filename);
    } finally {
      setLoadingFileId(null);
    }
  };

  const handleDownloadFile = async (file: TFileResponse) => {
    setLoadingFileId(file.id);
    try {
      const blob = await fetchFileBlob(file);
      if (!blob) return;
      triggerBlobDownload(blob, file.original_filename);
    } finally {
      setLoadingFileId(null);
    }
  };

  const handleViewOnboardingSummary = () => {
    if (!companyId) return;
    void navigate({
      to: "/app/companies/$company_id/onboarding/summary",
      params: { company_id: companyId },
    });
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
    }
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? "s" : ""} ago`;
    }
    const years = Math.floor(diffDays / 365);
    return `${years} year${years > 1 ? "s" : ""} ago`;
  };

  const formatDateLong = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const isRagUploaded = (file: TFileResponse): boolean => {
    return file.file_metadata?.ragflow_uploaded === true;
  };

  const displayedFiles = files.slice(0, maxItems);
  const hasMoreFiles = files.length > maxItems;

  // Only offer folders the company actually has files in — an empty "Chat" tab is a dead end,
  // and the set is small enough that a founder can see every folder at once.
  const tabs = [
    { id: ALL_TAB_ID, label: "All", files },
    ...KNOWLEDGE_CATEGORIES.map(({ id, tab }) => ({
      id: id as string,
      label: tab,
      files: files.filter((file) => file.file_type === id),
    })).filter((category) => category.files.length > 0),
  ];
  // A folder disappears when its last file goes, so never trust the stored id on its own.
  const activeTabId = tabs.some((tab) => tab.id === activeTab) ? activeTab : ALL_TAB_ID;
  const activeTabFiles = tabs.find((tab) => tab.id === activeTabId)?.files ?? files;

  const handleTabKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const current = tabs.findIndex((tab) => tab.id === activeTabId);
    const next = tabs[(current + step + tabs.length) % tabs.length];
    setActiveTab(next.id);
    document.getElementById(`knowledge-tab-${next.id}`)?.focus();
  };

  // Check if onboarding summary exists (we'll show it as a special item)
  // For now, we'll always show it if companyId exists, as it's a special knowledge item
  // In the future, we can check if the summary actually exists via API
  const hasOnboardingSummary = Boolean(companyId);

  // The summary is produced when onboarding completes, which is also the last
  // time the company record is touched during setup — so the company's
  // updated_at is the closest real timestamp we have for it.
  const company = companyId ? getCompanyById(companyId) : null;
  const companyTimestamp = company?.updated_at ?? company?.created_at;
  const onboardingSummaryDate = companyTimestamp ? formatDate(companyTimestamp) : null;

  return (
    <div className="space-y-3">
      {showTitle && (
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground sm:text-lg">Company Knowledge</h3>
          {(hasMoreFiles || files.length > 0) && (
            <button
              onClick={() => setViewAllOpen(true)}
              className="text-xs font-medium text-primary hover:underline sm:text-sm"
            >
              View all
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2.5 sm:space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-2.5 rounded-lg border border-border bg-card p-3 sm:gap-3 sm:p-4">
              <Skeleton className="h-9 w-9 flex-shrink-0 rounded-lg sm:h-10 sm:w-10" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-8 w-12 flex-shrink-0 rounded-md" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-2.5 text-xs text-destructive sm:p-3 sm:text-sm">
          {error}
        </div>
      ) : displayedFiles.length === 0 && !hasOnboardingSummary ? (
        <div className="rounded-lg border border-dashed border-border bg-muted p-4 text-center text-xs text-muted-foreground sm:p-6 sm:text-sm">
          No company knowledge files yet. Upload files to get started.
        </div>
      ) : (
        <div className="space-y-2.5 sm:space-y-3">
          {/* Onboarding Summary - same row style as file items */}
          {hasOnboardingSummary && (
            <div className="flex items-center gap-2.5 rounded-lg border border-border bg-card p-3 shadow-sm transition-shadow hover:shadow-md sm:gap-3 sm:p-4">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary sm:h-10 sm:w-10">
                <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground">Onboarding Summary</p>
                {onboardingSummaryDate ? (
                  <p className="text-xs text-muted-foreground">{onboardingSummaryDate}</p>
                ) : null}
              </div>
              <Button
                size="sm"
                onClick={handleViewOnboardingSummary}
                className="flex-shrink-0"
              >
                View
              </Button>
            </div>
          )}

          {/* File items */}
          {displayedFiles.map((file) => {
            const isUploaded = isRagUploaded(file);
            const { title, source } = describeFile(file);
            const indexingLabel = isUploaded ? "Available to Fundy's AI" : "Not yet available to Fundy's AI";
            return (
              <div
                key={file.id}
                className="flex items-center gap-2.5 rounded-lg border border-border bg-card p-3 shadow-sm transition-shadow hover:shadow-md sm:gap-3 sm:p-4"
              >
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary sm:h-10 sm:w-10">
                  <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{title}</p>
                  <div className="mt-0.5 flex min-w-0 items-center gap-2">
                    <p className="min-w-0 truncate text-xs text-muted-foreground">{formatDate(file.created_at)}</p>
                    {source ? (
                      <>
                        <span
                          className="flex-shrink-0 text-xs text-muted-foreground"
                          aria-hidden
                        >
                          •
                        </span>
                        <p className="min-w-0 truncate text-xs text-muted-foreground">{source}</p>
                      </>
                    ) : null}
                    <span
                      className="flex-shrink-0 text-xs text-muted-foreground"
                      aria-hidden
                    >
                      •
                    </span>
                    <span
                      className={`flex flex-shrink-0 items-center gap-1 text-xs ${isUploaded ? "font-medium text-foreground" : "text-muted-foreground"}`}
                      title={indexingLabel}
                    >
                      {isUploaded ? (
                        <CheckCircle
                          className="h-3.5 w-3.5 flex-shrink-0"
                          aria-hidden
                        />
                      ) : (
                        <Circle
                          className="h-3.5 w-3.5 flex-shrink-0"
                          aria-hidden
                        />
                      )}
                      <span className="sr-only">{indexingLabel}</span>
                    </span>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1 sm:gap-1.5">
                  <button
                    onClick={() => void handlePreviewFile(file)}
                    disabled={loadingFileId === file.id}
                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50 sm:h-8 sm:w-8"
                    title="Preview file"
                    aria-label={`Preview ${title}`}
                  >
                    {loadingFileId === file.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => void handleDownloadFile(file)}
                    disabled={loadingFileId === file.id}
                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent disabled:opacity-50 sm:h-8 sm:w-8"
                    title="Download file"
                    aria-label={`Download ${title}`}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Without the heading there is no "View all" control. It is the only way to reach
              both the files past maxItems and the folders, so it shows whenever files exist. */}
          {!showTitle && files.length > 0 && (
            <button
              onClick={() => setViewAllOpen(true)}
              className="w-full rounded-lg px-3 py-2 text-xs font-medium text-primary transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:text-sm"
            >
              {hasMoreFiles ? `View all ${files.length} documents` : "Browse by folder"}
            </button>
          )}
        </div>
      )}

      <Dialog
        open={viewAllOpen}
        onOpenChange={setViewAllOpen}
      >
        <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Company knowledge</DialogTitle>
          </DialogHeader>

          {files.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">
              No files yet. Upload a document, or attach one in a chat or a to-do.
            </p>
          ) : (
            <>
              <div
                role="tablist"
                aria-label="Filter files by where they came from"
                onKeyDown={handleTabKeyDown}
                className="-mx-1 flex flex-shrink-0 gap-1 overflow-x-auto px-1 pb-1"
              >
                {tabs.map((tab) => {
                  const isActive = tab.id === activeTabId;
                  return (
                    <button
                      key={tab.id}
                      id={`knowledge-tab-${tab.id}`}
                      role="tab"
                      type="button"
                      aria-selected={isActive}
                      aria-controls="knowledge-tabpanel"
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground"
                      }`}
                    >
                      {tab.label}
                      <span className={isActive ? "ml-1.5 opacity-80" : "ml-1.5 opacity-70"}>{tab.files.length}</span>
                    </button>
                  );
                })}
              </div>

              <div
                id="knowledge-tabpanel"
                role="tabpanel"
                aria-labelledby={`knowledge-tab-${activeTabId}`}
                tabIndex={0}
                className="min-h-0 flex-1 overflow-auto focus-visible:outline-none"
              >
                <ul className="space-y-2 pr-2">
                  {activeTabFiles.map((file) => {
                    const { title, source } = describeFile(file);
                    return (
                      <li
                        key={file.id}
                        className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm"
                      >
                        <span className="min-w-0 truncate font-medium text-foreground">{title}</span>
                        <span className="flex flex-shrink-0 items-center gap-2 text-xs text-muted-foreground">
                          {/* The source is what the tabs sort by, so it is noise once a folder is chosen. */}
                          {source && activeTabId === ALL_TAB_ID ? <span>{source}</span> : null}
                          <span>{formatDateLong(file.created_at)}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
